import "./zoom-gallery";

class MediaGallery extends HTMLElement {
  constructor() {
    super();

    this.activeVideos = [];

    window.loadSwiper.then((swiper) => {
      this.swiperLib = swiper;
      this._initCarousel();
    });
  }

  connectedCallback() {
    this.handleVariantChange = (e) => {
      const { variant } = e.detail;

      if (!variant?.featured_media) return;

      const mediaId = parseInt(variant.featured_media?.id);

      this.querySelectorAll("[data-media-id]").forEach((media) => {
        media.style.display =
          parseInt(media.dataset.mediaId) === mediaId ? null : "none";
      });

      this.gallerySwiper?.slideToLoop(0);
    };

    if (!this._isMobile) {
      this._initDesktopScrollCount();
    }

    document.addEventListener("variant:changed", this.handleVariantChange);
  }

  disconnectedCallback() {
    document.removeEventListener("variant:changed", this.handleVariantChange);
  }

  _initCarousel() {
    const { Swiper, Pagination } = this.swiperLib;
    
    const initSwiper = () => {
      if (this.gallerySwiper || !this._isMobile) return; 

      let swiperParams = {
        modules: [Pagination],
        slidesPerView: "auto",
        loop: false,
        speed: 400,
        watchSlidesProgress: true,
        centerInsufficientSlides: true,
        pagination: {
          el: this.querySelector(".swiper-pagination"),
          clickable: true,
        },
      };

      this.gallerySwiper = new Swiper(
        this.querySelector(`.${this._prefix}__media-container`),
        swiperParams,
      );

      this.gallerySwiper.on("slideChange", () => {
        const visibleSlides = this.gallerySwiper.slides.filter((slide) =>
          slide.classList.contains("swiper-slide-visible"),
        );
        this._selectVideo(visibleSlides);
      });

      this.gallerySwiper.slides.forEach((slide) => {
        slide.addEventListener("click", () => {
          // ignore videos
          if (slide.querySelector("video")) return;

          const img = slide.querySelector("img");
          if (!img) return;

          const toShopify2048 = (src) => {
            if (!src) return src;

            // split query
            const [base, query = ""] = src.split("?");
            const q = query ? `?${query}` : "";

            // remove existing Shopify size suffix (e.g. _800x, _1024x1024, _grande, etc.)
            // then append _2048x2048 before extension
            return base.replace(/(_\d+x\d+|_\d+x|_(pico|icon|thumb|small|compact|medium|large|grande|original|master))(?=\.[a-z]+$)/i, "")
                      .replace(/(\.[a-z]+)$/i, "_2048x2048$1") + q;
          };

          const highResSrc = toShopify2048(img.currentSrc || img.src);

          const zoomHtml = `
            <div class="zoom-modal zoom-modal--single" role="dialog" aria-modal="true">
              <button class="zoom-modal__close" aria-label="Close" data-close>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="11"
                  height="11"
                  viewBox="0 0 11 11"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.3"
                >
                  <path d="M1 1L10 10" stroke="black" stroke-miterlimit="10"/>
                  <path d="M10 1L1 10" stroke="black" stroke-miterlimit="10"/>
                </svg>
              </button>
              <div class="zoom-modal__scroll">
                <img
                  src="${highResSrc}"
                  alt="${img.alt || ""}"
                  loading="eager"
                  decoding="async"
                />
              </div>
            </div>
          `;

          this.dispatchEvent(
            new CustomEvent("markup-modal:open", {
              bubbles: true,
              detail: {
                html: zoomHtml,
                target: "zoom-modal",
              },
            })
          );
        });
     });
    };

    const destroySwiper = () => {
      if (this.gallerySwiper) {
        this.gallerySwiper.destroy(true, true);
        this.gallerySwiper = null;
      }
    };

    const handleResize = () => {
      if (this._isMobile) {
        initSwiper();
      } else {
        destroySwiper();
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize(); 
  }

  _initDesktopScrollCount() {
    if (this._isMobile) return;

    const mediaItems = this.querySelectorAll(`.${this._prefix}__media-wrapper`);
    const counterContainer = this.querySelector('[data-slide-count]');
    const contentWrapper = document.querySelector('.main-content-wrapper');
    const stickyHeader = document.querySelector('sticky-header');

    const toShopify4096 = (src) => {
      if (!src) return src;

      const [base, query = ""] = src.split("?");
      const q = query ? `?${query}` : "";

      return base
        // remove any existing Shopify size suffix
        .replace(
          /(_\d+x\d+|_\d+x|_(pico|icon|thumb|small|compact|medium|large|grande|original|master))(?=\.[a-z]+$)/i,
          ""
        )
        // append 4096 size
        .replace(/(\.[a-z]+)$/i, "_4096x4096$1") + q;
    };

    if (!mediaItems.length || !counterContainer || !contentWrapper || !stickyHeader) return;

    let lastFocusedItem = null;

    mediaItems.forEach((item) => {
      item.setAttribute("tabindex", "0");
      item.setAttribute("role", "button");

      item.addEventListener("click", () => {
        // Ignore videos
        if (item.querySelector("video")) return;

        const img = item.querySelector("img");
        if (!img) return;
        lastFocusedItem = item;
        const highResSrc = toShopify4096(img.currentSrc || img.src);

        const zoomHtml = `
          <div class="zoom-modal zoom-modal--single" role="dialog" aria-modal="true">
            <img
              src="${highResSrc}"
              alt="${img.alt || ""}"
              loading="eager"
              decoding="async"
            />
          </div>
        `;

        this.dispatchEvent(
          new CustomEvent("markup-modal:open", {
            bubbles: true,
            detail: {
              html: zoomHtml,
              target: "zoom-modal",
            },
          })
        );
      });
      // Keyboard support: Enter + Space
      item.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault(); // prevent scrolling for Space
          item.click(); // trigger the existing click handler
        }
      });
    });

    const current = document.createElement('span');
    const total = document.createElement('span');
    current.className = 'current-index';
    total.className = 'total-index';
    total.textContent = mediaItems.length;
    current.textContent = mediaItems.length ? '1' : ' ';

    counterContainer.innerHTML = '';
    counterContainer.appendChild(current);
    counterContainer.insertAdjacentHTML('beforeend', ' / ');
    counterContainer.appendChild(total);

    const updateIndex = () => {
      const triggerPoint = window.innerHeight * 0.1; 

      let activeIndex = 1;

      mediaItems.forEach((item, i) => {
        const rect = item.getBoundingClientRect();

        if (rect.top <= triggerPoint) {
          activeIndex = i + 1;
        }
      });

      current.textContent = activeIndex;
    };

    let scrollTicking = false;
    const onScroll = () => {
      if (!scrollTicking) {
        scrollTicking = true;
        requestAnimationFrame(() => {
          updateIndex();
          scrollTicking = false;
        });
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    updateIndex();

    const updateCounterPosition = () => {
      const wrapperRect = contentWrapper.getBoundingClientRect();
      const headerHeight = stickyHeader.getHeaderHeight?.() || 0;
      const offsetTop = wrapperRect.top + window.pageYOffset + 16 - headerHeight;

      counterContainer.style.position = 'sticky';
      counterContainer.style.top = `${Math.max(offsetTop, 16)}px`;
    };

    updateCounterPosition();

    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        updateCounterPosition();
        updateIndex();
      }, 120);
    });

    document.addEventListener("markup-modal:closed", () => {
      if (lastFocusedItem) lastFocusedItem.focus();
    });
  }

  _selectVideo(els) {
    this.activeVideos.forEach((video) => video.pause());

    this.activeVideos = [];

    els.forEach((el) => {
      const video = el.querySelector("video");

      if (video) {
        this.activeVideos.push(video);
        video.play();
      }
    });
  }

  get _zoomMarkup() {
    return this.querySelector("[data-zoom-markup]");
  }

  get _prefix() {
    return this.dataset.classPrefix;
  }

  get _isMobile() {
    return window.matchMedia("(max-width: 1023px)").matches;
  }
}

if (!customElements.get("media-gallery")) {
  customElements.define("media-gallery", MediaGallery);
}
